import { defineStore } from 'pinia'
import { ref } from 'vue'
import { Preferences } from '@capacitor/preferences'
import {
    getNotificationPreferences,
    updateNotificationPreferences
} from '@/services/api/notifications'
import type { NotificationPreferences } from '@/types/notifications'

const NOTIFICATION_SETTINGS_KEY = 'notification_settings'

/**
 * Notification reminder configuration
 */
export interface NotificationReminderConfig {
    enabled: boolean
    minutesOffset: number // 1-60 minutes
}

/**
 * All notification settings
 */
export interface NotificationSettings {
    checkInBefore: NotificationReminderConfig
    checkInAfter: NotificationReminderConfig
    checkOutBefore: NotificationReminderConfig
    checkOutAfter: NotificationReminderConfig
}

/**
 * Default notification settings
 */
const DEFAULT_SETTINGS: NotificationSettings = {
    checkInBefore: {
        enabled: true,
        minutesOffset: 5 // 5 minutes before work start
    },
    checkInAfter: {
        enabled: true,
        minutesOffset: 10 // 10 minutes after work start
    },
    checkOutBefore: {
        enabled: true,
        minutesOffset: 1 // 1 minute before work end
    },
    checkOutAfter: {
        enabled: true,
        minutesOffset: 30 // 30 minutes after work end
    }
}

export const useNotificationSettingsStore = defineStore('notificationSettings', () => {
    // Reactive state
    const checkInBefore = ref<NotificationReminderConfig>({ ...DEFAULT_SETTINGS.checkInBefore })
    const checkInAfter = ref<NotificationReminderConfig>({ ...DEFAULT_SETTINGS.checkInAfter })
    const checkOutBefore = ref<NotificationReminderConfig>({ ...DEFAULT_SETTINGS.checkOutBefore })
    const checkOutAfter = ref<NotificationReminderConfig>({ ...DEFAULT_SETTINGS.checkOutAfter })
    const isLoading = ref(false)

    /**
     * Load notification settings from server
     * Falls back to local cache if server request fails
     */
    async function loadSettings(workerId?: number): Promise<void> {
        isLoading.value = true
        try {
            if (workerId) {
                // Fetch from server
                const preferences = await getNotificationPreferences(workerId)
                checkInBefore.value = preferences.checkInBefore
                checkInAfter.value = preferences.checkInAfter
                checkOutBefore.value = preferences.checkOutBefore
                checkOutAfter.value = preferences.checkOutAfter

                // Cache locally
                await saveToLocalCache(preferences)
            } else {
                // Load from local cache (fallback)
                await loadFromLocalCache()
            }
        } catch (error) {
            console.error('Failed to load notification settings from server, using cache:', error)
            // Fall back to local cache
            await loadFromLocalCache()
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Load settings from local cache
     */
    async function loadFromLocalCache(): Promise<void> {
        try {
            const { value } = await Preferences.get({ key: NOTIFICATION_SETTINGS_KEY })
            if (value) {
                const settings = JSON.parse(value) as NotificationSettings
                checkInBefore.value = settings.checkInBefore ?? { ...DEFAULT_SETTINGS.checkInBefore }
                checkInAfter.value = settings.checkInAfter ?? { ...DEFAULT_SETTINGS.checkInAfter }
                checkOutBefore.value = settings.checkOutBefore ?? {
                    ...DEFAULT_SETTINGS.checkOutBefore
                }
                checkOutAfter.value = settings.checkOutAfter ?? { ...DEFAULT_SETTINGS.checkOutAfter }
            }
        } catch (error) {
            console.error('Failed to load from local cache:', error)
        }
    }

    /**
     * Save settings to local cache
     */
    async function saveToLocalCache(preferences: NotificationPreferences): Promise<void> {
        try {
            await Preferences.set({
                key: NOTIFICATION_SETTINGS_KEY,
                value: JSON.stringify(preferences)
            })
        } catch (error) {
            console.error('Failed to save to local cache:', error)
        }
    }

    /**
     * Save notification settings to server
     * Also saves to local cache
     */
    async function saveSettings(workerId: number): Promise<void> {
        isLoading.value = true
        try {
            const preferences: NotificationPreferences = {
                checkInBefore: checkInBefore.value,
                checkInAfter: checkInAfter.value,
                checkOutBefore: checkOutBefore.value,
                checkOutAfter: checkOutAfter.value
            }

            // Save to server
            await updateNotificationPreferences(workerId, preferences)

            // Save to local cache
            await saveToLocalCache(preferences)
        } catch (error) {
            console.error('Failed to save notification settings:', error)
            throw error
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Update check-in before reminder settings
     * @param workerId Worker ID (required for server update)
     * @param enabled Whether the reminder is enabled
     * @param minutesOffset Minutes offset from work start
     */
    async function updateCheckInBefore(
        workerId: number,
        enabled: boolean,
        minutesOffset?: number
    ): Promise<void> {
        checkInBefore.value.enabled = enabled
        if (minutesOffset !== undefined) {
            checkInBefore.value.minutesOffset = minutesOffset
        }
        await saveSettings(workerId)
    }

    /**
     * Update check-in after reminder settings
     */
    async function updateCheckInAfter(
        workerId: number,
        enabled: boolean,
        minutesOffset?: number
    ): Promise<void> {
        checkInAfter.value.enabled = enabled
        if (minutesOffset !== undefined) {
            checkInAfter.value.minutesOffset = minutesOffset
        }
        await saveSettings(workerId)
    }

    /**
     * Update check-out before reminder settings
     */
    async function updateCheckOutBefore(
        workerId: number,
        enabled: boolean,
        minutesOffset?: number
    ): Promise<void> {
        checkOutBefore.value.enabled = enabled
        if (minutesOffset !== undefined) {
            checkOutBefore.value.minutesOffset = minutesOffset
        }
        await saveSettings(workerId)
    }

    /**
     * Update check-out after reminder settings
     */
    async function updateCheckOutAfter(
        workerId: number,
        enabled: boolean,
        minutesOffset?: number
    ): Promise<void> {
        checkOutAfter.value.enabled = enabled
        if (minutesOffset !== undefined) {
            checkOutAfter.value.minutesOffset = minutesOffset
        }
        await saveSettings(workerId)
    }

    /**
     * Reset to default settings
     */
    async function resetToDefaults(workerId: number): Promise<void> {
        checkInBefore.value = { ...DEFAULT_SETTINGS.checkInBefore }
        checkInAfter.value = { ...DEFAULT_SETTINGS.checkInAfter }
        checkOutBefore.value = { ...DEFAULT_SETTINGS.checkOutBefore }
        checkOutAfter.value = { ...DEFAULT_SETTINGS.checkOutAfter }
        await saveSettings(workerId)
    }

    /**
     * Get all settings as a single object
     */
    function getAllSettings(): NotificationSettings {
        return {
            checkInBefore: checkInBefore.value,
            checkInAfter: checkInAfter.value,
            checkOutBefore: checkOutBefore.value,
            checkOutAfter: checkOutAfter.value
        }
    }

    return {
        // State
        checkInBefore,
        checkInAfter,
        checkOutBefore,
        checkOutAfter,
        isLoading,

        // Actions
        loadSettings,
        saveSettings,
        updateCheckInBefore,
        updateCheckInAfter,
        updateCheckOutBefore,
        updateCheckOutAfter,
        resetToDefaults,
        getAllSettings
    }
})
